import { computed, inject, provide, ref } from "vue";
import { generateUID } from "@/lib/helpers.js";

import { Address } from "@/typings/address";

class AddressItem extends Address {
  phones: Array<Address["phones"][0] & { isNew: boolean }> = [];
  emails: Array<Address["emails"][0] & { isNew: boolean }> = [];

  constructor(params: Partial<AddressItem> = {}) {
    super(params);
    Object.assign(this, params);
  }
}

export const injectKey = Symbol();

export function useSelectedAddress(provideState = false) {
  const selectedAddress = ref<AddressItem>(new AddressItem());

  const additionalEmails = computed(() => {
    return selectedAddress.value.emails.filter((_, index) => index !== 0);
  });

  const additionalPhones = computed(() => {
    return selectedAddress.value.phones.filter((_, index) => index !== 0);
  });

  function getEmail(id) {
    if (id === "primary") {
      return selectedAddress.value.emails[0];
    }
    return selectedAddress.value.emails.find((email) => email.id === id);
  }

  function setEmail(id, val) {
    let index = -1;
    if (id === "primary") {
      index = 0;
    } else {
      index = selectedAddress.value.emails.findIndex(
        (email) => email.id === id
      );
    }
    selectedAddress.value.emails[index] = val;
  }

  function getPhone(id) {
    if (id === "primary") {
      return selectedAddress.value.phones[0];
    }
    return selectedAddress.value.phones.find((phone) => phone.id === id);
  }

  function setPhone(id, val) {
    let index = -1;
    if (id === "primary") {
      index = 0;
    } else {
      index = selectedAddress.value.phones.findIndex(
        (phone) => phone.id === id
      );
    }
    selectedAddress.value.phones[index] = val;
  }

  function addPhone(primary = false) {
    const newData = {
      isNew: true,
      id: generateUID(),
      phone_number: "",
      type: "",
      label: ""
    };
    if (primary) {
      selectedAddress.value.phones.unshift(newData);
    } else {
      selectedAddress.value.phones.push(newData);
    }
  }

  function addEmail(primary = false) {
    const newData = {
      isNew: true,
      id: generateUID(),
      email_address: "",
      label: ""
    };
    if (primary) {
      selectedAddress.value.emails.unshift(newData);
    } else {
      selectedAddress.value.emails.push(newData);
    }
  }

  function removePhone(id) {
    const index = selectedAddress.value.phones.findIndex(
      (phone) => phone.id === id
    );
    selectedAddress.value.phones.splice(index, 1);
  }

  function removeEmail(id) {
    const index = selectedAddress.value.emails.findIndex(
      (email) => email.id === id
    );
    selectedAddress.value.emails.splice(index, 1);
  }

  function toJSON() {
    // Had to parse + stringify so we don't accidentally copy by ref
    const newAddress = new AddressItem(
      JSON.parse(JSON.stringify(selectedAddress.value))
    );
    if (!selectedAddress.value.receives_communication) {
      newAddress.emails = newAddress.emails.filter((email) => !email.isNew);
      newAddress.phones = newAddress.phones.filter((phone) => !phone.isNew);
    } else {
      // all new emails + phones should not have ids
      newAddress.emails
        .filter((email) => email.isNew)
        .forEach((email) => {
          email.id = null;
        });
      newAddress.phones
        .filter((phone) => phone.isNew)
        .forEach((phone) => {
          phone.id = null;
        });
    }
    return newAddress;
  }

  const addressInterface = {
    selectedAddress,
    additionalEmails,
    additionalPhones,
    addPhone,
    getPhone,
    setPhone,
    removePhone,
    addEmail,
    removeEmail,
    setEmail,
    getEmail,
    toJSON
  };

  if (provideState) provide(injectKey, addressInterface);
  return addressInterface;
}

export const injectUseSelectedAddress = () =>
  inject<ReturnType<typeof useSelectedAddress>>(injectKey);
